import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { useQueryClient } from '@tanstack/react-query';
import { useGetTopicDropdownValues } from 'hooks/useGetTopicDropdownValues';
import { useGetOrganisationalUnitDropdownValues } from 'hooks/useOrganisationalUnitsValues';
import ContactNote from 'types/ContactNote';
import contactNoteService from 'services/contactNoteService';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import { Box, Grid, TableBody, TablePagination, Typography } from '@mui/material';
import DeprecatedTableHead, { HeadCellContext, Order } from 'components/Common/DeprecatedTableHead';
import { GlobalState } from 'reducers/stateTypes';
import { SkeletonTableRow } from 'components/Common/SkeletonTableRow';
import { initContactNotes } from 'reducers/contactNoteReducer';
import {
  setCnError,
  setCnFilters,
  setCnPagination,
  setCnSubjectSearch,
  setLoadingStatus,
  setNoteDisplayDialog,
  setSelectedNotes as setSelectedNotesAction,
  setToastMessage,
  toggleContactNoteDrawer,
} from 'reducers/contactNoteListReducer';
import { TextFilter } from 'components/Common/TextFilter';
import CustomDateRangePicker from 'components/Common/CustomDateRangePicker';
import useKeyFilter, { Filters } from 'hooks/useKeyFilter';
import { useOpenContactNote } from 'hooks/useOpenContactNote';
import ContactNoteRows from 'views/ContactNotes/components/ContactNoteRows';
import { ContactNoteHeadCells, ContactNoteZafireHeadCells } from 'views/ContactNotes/ContactNoteHeadcells';
import { ZfBox, ZfIcon, ZfInput, ZfPopover, ZfStack, ZfTextButton, ZfToast, ZfTypography } from '@zafire/react';
import { EnvConfig } from 'config/envConfig';
import { User } from 'types/User';
import { EnhancedContactNotesFormDialog } from 'views/ContactNoteFormDialog/zafire/components/EnhancedFormDialog';
import { AgGridReact } from 'ag-grid-react';
import { themeZafire } from '@zafire/ag-grid-enterprise';
import { formatDate, isDigit } from 'utils/stringUtil';
import { translateStatusEnum } from 'utils/contactNoteUtil';
import { ColDef, RowClickedEvent, SideBarDef } from 'ag-grid-enterprise';
import PartnerColumnFilter from 'views/ContactNoteHome/zafire/components/PartnerColumnFilter';
import contactNoteDialogService from 'services/contactNoteDialogService';
import { DropdownOptionActiveState } from 'components/Common/ValidatedDropdown';
import { AdditionalAttendeeFilter } from 'views/ContactNoteHome/zafire/components/AdditionalAttendeeFilter';
import {
  ContactNoteDialogLabelStatus,
  ContactNoteStatusOptions,
} from 'views/ContactNoteFormDialog/zafire/components/data/ContactNoteDialogStatus';
import CreatorColumnFilter from 'views/ContactNoteHome/zafire/components/CreatorColumnFilter';
import ErrorDialog from 'views/ContactNoteFormDialog/zafire/components/ErrorDialog';
import DateFilter from 'views/ContactNoteHome/zafire/components/DateFilter';

import { DisplayDialogs } from './ContactNotesContainer';
import CheckboxCellRenderer from './zafire/CheckboxCellRenderer';
import { LinkedNotes } from 'views/ContactNotes/zafire/LinkedNotes';

/**
 * Component to display the contact notes list
 */
const ContactNotes = ({
  usedInBr = false,
  isFederated = false,
  linkedNoteIds = [],
  linkedNoteUIDs = [],
  allowUnlink = false,
  brUId = null,
  partnerUId = null,
}: {
  usedInBr?: boolean;
  isFederated?: boolean;
  /** @deprecated Use `linkedNoteUIDs` instead. Ignored when `linkedNoteUIDs` is non-empty. */
  linkedNoteIds?: string[];
  linkedNoteUIDs?: string[];
  allowUnlink?: boolean;
  brUId?: string | null;
  partnerUId?: string | null;
}) => {
  // linkedNoteUIDs (matched against ContactNote.uid) takes priority over the deprecated
  // linkedNoteIds (matched against ContactNote.contactNoteId) when both are supplied.
  const hasLinkedNoteUIDs = linkedNoteUIDs.length > 0;
  const effectiveLinkedIds = hasLinkedNoteUIDs ? linkedNoteUIDs : linkedNoteIds;
  const linkedIdField: 'uid' | 'contactNoteId' = hasLinkedNoteUIDs ? 'uid' : 'contactNoteId';
  // Content-based key so a host passing a fresh array on every render does not retrigger the fetch.
  const effectiveLinkedKey = effectiveLinkedIds.join('|');
  const getLinkKey = useCallback((note: ContactNote) => note[linkedIdField] ?? '', [linkedIdField]);

  const gridRef = useRef<AgGridReact>(null);
  const queryClient = useQueryClient();
  const customFiltersRef = useRef<Map<string, any>>(new Map());
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorObject, setErrorObject] = useState<any>(undefined);

  const errorMessage: string = useSelector((state: GlobalState) => state.contactNoteList.error);
  const contactNotes: ContactNote[] = useSelector((state: GlobalState) => state.contactNotes);
  const drawerOpen: boolean = useSelector((state: GlobalState) => state.contactNoteList.drawerOpen);
  const openContactNote = useOpenContactNote();

  const isLoading: boolean = useSelector((state: GlobalState) => state.contactNoteList.loading);
  const subjectFilter: string = useSelector((state: GlobalState) => state.contactNoteList.subjectSearch);
  const dateRange: any[] = useSelector((state: GlobalState) => state.contactNoteList.dateRange);
  const page: number = useSelector((state: GlobalState) => state.contactNoteList.paginationPage);
  const employeeOptions = useSelector((state: GlobalState) => state.resources.employeeOptions);
  const toastMessage = useSelector((state: GlobalState) => state.contactNoteList.toastMessage);
  const selectedNotes: ContactNote[] = useSelector((state: GlobalState) => state.contactNoteList.selectedNotes);

  const [order, setOrder] = React.useState<Order>('desc');
  const [orderBy, setOrderBy] = React.useState<string>('startDateTime');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterModel, setFilterModel] = useState({});

  // Generate unique session ID for this screen session
  const [screenSessionId] = useState(() => {
    // Generate a unique session ID that persists for this component instance
    // but changes when the component is remounted
    const sessionId = `contact-notes-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    // eslint-disable-next-line no-console
    console.log('ContactNotes Session ID generated:', sessionId);
    return sessionId;
  });

  const dispatch = useDispatch();
  const { techId } = useParams<{ techId: string }>();
  const filters: Filters = useSelector((state: GlobalState) => state.contactNoteList.filters);
  const [filteredContactNotes] = useKeyFilter(contactNotes, filters);
  const displayDialog: string = useSelector((state: GlobalState) => state.contactNoteList.displayDialog);
  const user: User = useSelector((state: GlobalState) => state.user.userDetails);
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState('');
  const [unlinkedNoteIds, setUnlinkedNoteIds] = useState<Set<string>>(new Set());

  const contactTypeOptions = useSelector((state: GlobalState) => state.resources.contactTypeOptions);
  const { data: topicsGridOptions = [] } = useGetTopicDropdownValues(true);
  const { data: organisationalUnitDropdownValues = [] } = useGetOrganisationalUnitDropdownValues();

  const organisationalUnitOptions = useMemo(() => {
    const values = new Set<string>();

    contactNotes.forEach((note) => {
      note.organisationalUnits?.forEach((ou) => {
        if (ou) {
          values.add(ou);
        }
      });
    });

    return Array.from(values).sort();
  }, [contactNotes]);

  const isResetTableDisabled = useMemo(() => {
    return Object.keys(filterModel).length === 0 && searchInput === '';
  }, [filterModel, searchInput]);
  const contactTypeFilteredOptions = useMemo(() => {
    if (contactTypeOptions?.length === 0) {
      return [];
    }
    const list = contactTypeOptions.filter((item) => item.isActive === DropdownOptionActiveState.YES);
    return list;
  }, [contactTypeOptions]);

  const contactTypeLabel = useCallback(
    (contactType: string) => {
      if (contactTypeOptions.length > 0) {
        return contactTypeOptions.find((item) => item.value === contactType)?.label || '';
      }
      return '-';
    },
    [contactTypeOptions],
  );

  const topicsLabel = useCallback((topicsLabels: string[] | undefined) => {
    if (!topicsLabels || topicsLabels.length === 0) {
      return '-';
    }
    return topicsLabels.join(', ');
  }, []);

  const searchNotes = useCallback(
    async (agGridRequest: any) => {
      try {
        const res = await contactNoteDialogService.searchNotesAgGridV2(agGridRequest);
        return res;
      } catch (error) {
        setErrorObject(error);
        setShowErrorDialog(true);
        throw error;
      }
    },
    [contactNoteDialogService],
  );

  useEffect(() => {
    if (isFederated) {
      const fetchLinkedNotes = async () => {
        try {
          const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const linkedIds = effectiveLinkedKey.split('|');
          const filterModel = {
            [linkedIdField]: {
              filterType: 'text',
              operator: 'OR',
              conditions: linkedIds.map((id) => ({
                filterType: 'text',
                type: 'equals',
                filter: id,
              })),
            },
          };

          const agGridRequest = {
            startRow: 0,
            endRow: linkedIds.length,
            filterModel,
            sortModel: [],
            query: '',
            timezone: userTimezone,
            ...(brUId && brUId.length > 0
              ? { brUid: brUId }
              : partnerUId && partnerUId.length > 0
                ? { partnerUid: partnerUId }
                : {}),
          };

          const res = await searchNotes(agGridRequest);
          const validNotes = res.rows.filter((note: ContactNote) => note !== null) as ContactNote[];
          dispatch(setSelectedNotesAction(validNotes));
        } catch (error) {
          setErrorObject(error);
          setShowErrorDialog(true);
        }
      };

      if (effectiveLinkedKey.length > 0) {
        fetchLinkedNotes();
      }
    }
  }, [isFederated, effectiveLinkedKey, linkedIdField, dispatch]);

  const showDialog = useMemo(() => {
    return displayDialog === DisplayDialogs.NEW_NOTE_VIEW || displayDialog === DisplayDialogs.EDIT_VIEW;
  }, [displayDialog]);

  const hasFilter = useMemo(() => {
    return Object.keys(filters).length > 0;
  }, [filters]);

  /**
   * Loads the notes of the shown partner
   * TODO: needs to check the usage of this
   */
  async function getMyNotes() {
    dispatch(setLoadingStatus(true));
    try {
      const myNotes = await contactNoteService.getMyNotes(
        null,
        false,
        subjectFilter,
        dateRange,
        null,
        techId,
        usedInBr,
      );

      dispatch(initContactNotes(myNotes));
      dispatch(setCnError(''));
    } catch (error: any) {
      if (error?.response?.status === 404) {
        if (subjectFilter === '') {
          dispatch(setCnError(t('contactNote.search.noContactNote')));
        } else {
          dispatch(setCnError(t('contactNote.search.noContactNoteFound')));
        }
      } else {
        dispatch(setCnError('Error loading contact notes: ' + error));
      }
    } finally {
      dispatch(setLoadingStatus(false)); // Always stop loading
    }
  }

  const onFilterChanged = () => {
    const api = gridRef.current?.api;
    if (api) {
      setFilterModel(api.getFilterModel());
    }
  };

  //Load the contactNote list. Set the feedback error message if no contactNotes are found or an error is encountered.
  useEffect(() => {
    if (
      EnvConfig.config.createContactNoteNewUiEnabled !== undefined &&
      !EnvConfig.config.createContactNoteNewUiEnabled
    ) {
      dispatch(setLoadingStatus(true));
      getMyNotes();
    }
  }, [subjectFilter, dateRange, setLoadingStatus, dispatch, EnvConfig.config.createContactNoteNewUiEnabled]);

  const handleChangePage = (_: unknown, newPage: number) => {
    dispatch(setCnPagination(newPage));
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    dispatch(setCnPagination(0));
  };

  const additionalAttendeesLabel = useCallback(
    (contactNote: ContactNote) => {
      const validAttendee = contactNote?.additionalAttendees?.filter((item) => item && item.userId) || [];

      return (
        validAttendee
          .map((item) => {
            if (item.fullName) {
              return `${item.fullName} (${item.userId.toLowerCase()})`;
            }

            const employeeName = employeeOptions.find((co) => co.value === item.userId.toLowerCase());
            if (employeeName) {
              return employeeName.label;
            }
            return `N/A (${item.userId})`;
          })
          .join(', ') || '-'
      );
    },
    [employeeOptions],
  );

  const NoRowsOverlay = useCallback(
    ({ filterModel, subjectFilter }: { filterModel: any; subjectFilter: string }) => {
      const hasFilter = Object.keys(filterModel).length > 0 || subjectFilter !== '';

      if (hasFilter) {
        return (
          <ZfBox>
            <ZfStack spacing="small" style={{ width: 500, alignItems: 'center' }}>
              <ZfIcon name="search_off" size="extra-extra-large" />
              <ZfTypography color="normal" variant="title-large">
                {t('contactNote.form.table.noFilteredContactNote')}
              </ZfTypography>
              <ZfTypography color="muted" variant="body-medium">
                {t('contactNote.form.table.noFilteredSubLabel')}
              </ZfTypography>
              {/* <ZfTextButton
              size="medium"
              hierarchy="secondary"
              onClick={() => {
                gridRef.current?.api.setFilterModel(null);
                dispatch(setCnSubjectSearch(''));
                setSearchInput('');
              }}
            >
              {t('contactNote.form.table.clearFilters')}
            </ZfTextButton> */}
            </ZfStack>
          </ZfBox>
        );
      }

      return (
        <ZfBox>
          <ZfStack spacing="small" style={{ width: 500, alignItems: 'center' }}>
            <ZfIcon name="search" size="extra-extra-large" />
            <ZfTypography color="normal" variant="title-large">
              {t('contactNote.home.startSearching')}
            </ZfTypography>
            <ZfTypography color="muted" variant="body-medium" alignment="center">
              {t('contactNote.home.startSearchSublabel')}
            </ZfTypography>
          </ZfStack>
        </ZfBox>
      );
      //this is showing creation flow
      // return (
      //   <ZfBox>
      //     <ZfStack spacing="small" style={{ width: 500, alignItems: 'center' }}>
      //       <ZfIcon name="search_off" size="extra-extra-large" />
      //       <ZfTypography color="normal" variant="title-large">
      //         {t('contactNote.form.table.noData')}
      //       </ZfTypography>
      //       <ZfTextButton
      //         data-testid="new-note-button"
      //         disabled={isLoading || !user.hasNoteCreationAccess}
      //         size="medium"
      //         hierarchy="secondary"
      //         onClick={() => dispatch(setNoteDisplayDialog(DisplayDialogs.NEW_NOTE_VIEW))}
      //       >
      //         {t('contactNote.createContactNote')}
      //       </ZfTextButton>
      //     </ZfStack>
      //   </ZfBox>
      // );
    },
    [isLoading, user?.hasNoteCreationAccess, gridRef, dispatch],
  );

  const relatedPartnersLabel = useCallback((contactNote: ContactNote) => {
    let label = contactNote?.relatedPartners?.map((item) => item.fullName)?.join(', ') || '';
    if (contactNote?.relatedPartners?.length === 0 && !contactNote?.otherPartner) {
      return '-';
    }
    if (contactNote?.relatedPartners?.length > 0 && contactNote?.otherPartner) {
      label += ', ';
    }
    if (contactNote.otherPartner) {
      label += 'Other';
    }
    return label;
  }, []);

  const relatedBRsLabel = useCallback((contactNote: ContactNote) => {
    if (contactNote?.relatedBRs?.length === 0) {
      return '-';
    }
    return contactNote?.relatedBRs
      ?.map((item) =>
        isDigit(item.masterNumber?.slice(0, 8))
          ? `${item?.masterNumber?.slice(0, 4)}.${item?.masterNumber?.slice(4)}`
          : item.masterNumber || `Prospecting BR`,
      )
      ?.join(', ');
  }, []);

  const sideBar: SideBarDef = useMemo(() => {
    return {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          minWidth: 225,
          maxWidth: 225,
          width: 225,
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivotMode: true,
          },
        },
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
          minWidth: 180,
          maxWidth: 400,
          width: 250,
        },
      ],
      position: 'left',
    };
  }, []);

  const convertRowData = useCallback(
    (data: ContactNote[]) => {
      if (data.length >= 0) {
        return data.map((item) => ({
          id: item.id,
          additionalAttendees: additionalAttendeesLabel(item),
          contactNoteId: item.contactNoteId || '-',
          uid: item.uid,
          startDateTime: item.startDateTime ? formatDate(item.startDateTime) : '-',
          relatedPartners: relatedPartnersLabel(item),
          relatedBRs: relatedBRsLabel(item),
          subject: item.subject || '-',
          notes: item.notes || '-',
          contactType: item.contactType,
          topics: item.topics ?? [],
          topicsLabels: item.topicsLabels ?? [],
          topicOther: item.topicOther ?? '',
          qualifiedClientContact: item.qualifiedClientContact,
          createdByFullName: item.createdByFullName
            ? `${item.createdByFullName}${item.createdBy ? ` (${item.createdBy})` : ''}`
            : item.createdBy || 'N/A',
          organisationalUnits: item.organisationalUnits ?? [],
          source: item.source || '-',
          tripId: item.tripId || '-',
          attachments: item.attachments || [], // Pass raw array, cellRenderer will convert to Yes/No
          status: { status: item.status, tripId: item.tripId, createdDateTime: item.createdDateTime },
          __row: item, // Keep original object for easy access later
        }));
      }

      return [];
    },
    [relatedPartnersLabel, relatedBRsLabel, contactTypeLabel, additionalAttendeesLabel],
  );

  useEffect(() => {
    if (!drawerOpen && gridRef.current && gridRef.current.api) {
      const selectedNodes = gridRef.current?.api?.getSelectedNodes();
      const hasSelection = !!selectedNodes?.length;
      if (hasSelection) {
        gridRef.current?.api.deselectAll();
      }
    }
  }, [drawerOpen, gridRef]);

  /**
   * Deselects the grid rows matching the given contactNoteIds (or all rows, if 'all' is passed)
   * and forces the checkbox cells to re-render against the latest selection state.
   */
  const deselectRowsAndRefresh = useCallback((idsToDeselect: string[] | 'all') => {
    const api = gridRef.current?.api;
    if (!api) return;

    if (idsToDeselect === 'all') {
      api.deselectAll();
    } else if (idsToDeselect.length > 0) {
      const idSet = new Set(idsToDeselect);
      api.forEachNode((node) => {
        if (idSet.has(node.data?.contactNoteId)) {
          node.setSelected(false, false);
        }
      });
    }

    api.refreshCells({ force: true });
  }, []);

  const handleNotesSelection = (event: RowClickedEvent) => {
    const note = event.data.__row as ContactNote;
    if (!allowUnlink && event.node.isSelected() && selectedNotes.some((n) => n.contactNoteId === note.contactNoteId)) {
      return;
    }
    if (allowUnlink && selectedNotes.some((n) => n.contactNoteId === note.contactNoteId)) {
      dispatch(setSelectedNotesAction(selectedNotes.filter((n) => n.id !== note.id)));
      setUnlinkedNoteIds((prev) => new Set(prev).add(note.contactNoteId));
      deselectRowsAndRefresh([note.contactNoteId]);
      return;
    }

    if (event.node.isSelected()) {
      const noteToSelect = {
        ...note,
        linkingStatus: 'pending' as const,
      };
      dispatch(setSelectedNotesAction([...new Set([...selectedNotes, noteToSelect])]));
    } else {
      dispatch(setSelectedNotesAction(selectedNotes.filter((n) => n.id !== note.id)));
    }
  };

  const onRowClicked = (event: RowClickedEvent) => {
    if (isFederated) {
      handleNotesSelection(event);
      return;
    }

    const selectedContactNote = event.data?.__row;
    if (selectedContactNote) {
      openContactNote(selectedContactNote);
    }
  };

  const handlePillDelete = useCallback(
    (idToDelete: string) => {
      dispatch(setSelectedNotesAction(selectedNotes.filter((n) => n.contactNoteId !== idToDelete)));

      const deletedNote = selectedNotes.find((n) => n.contactNoteId === idToDelete);
      const wasOriginallyLinked = !!deletedNote && effectiveLinkedIds.includes(getLinkKey(deletedNote));
      if (wasOriginallyLinked) {
        setUnlinkedNoteIds((prev) => new Set(prev).add(idToDelete));
      }
      deselectRowsAndRefresh([idToDelete]);
    },
    [dispatch, selectedNotes, effectiveLinkedIds, getLinkKey, deselectRowsAndRefresh],
  );

  const handleClearAll = useCallback(() => {
    if (allowUnlink) {
      dispatch(setSelectedNotesAction([]));
      const originallyLinkedContactNoteIds = selectedNotes
        .filter((note) => effectiveLinkedIds.includes(getLinkKey(note)))
        .map((note) => note.contactNoteId);
      setUnlinkedNoteIds((prev) => new Set([...prev, ...originallyLinkedContactNoteIds]));
      deselectRowsAndRefresh('all');
    } else {
      const notesToKeep = selectedNotes.filter((note) => note.linkingStatus !== 'pending');
      const notesToRemove = selectedNotes.filter((note) => note.linkingStatus === 'pending');
      dispatch(setSelectedNotesAction(notesToKeep));
      deselectRowsAndRefresh(notesToRemove.map((n) => n.contactNoteId));
    }
  }, [dispatch, allowUnlink, selectedNotes, effectiveLinkedIds, getLinkKey, deselectRowsAndRefresh]);

  const tooltipValueGetter = useCallback(
    (params: any) => {
      if (!params.value) return null; // no tooltip if empty
      if (typeof params.value === 'object') {
        return (
          translateStatusEnum(params.value.status, params.value.createdDateTime, params.value.tripId) ||
          (params.value.status === 'DRAFT' ? 'EDITABLE' : params.value.status)
        );
      }
      if (typeof params.value === 'string') {
        return params.value.length > 300 ? params.value.substring(0, 300) + '...' : params.value;
      }
      return String(params.value); // must return string
    },
    [translateStatusEnum],
  );

  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      const q = event?.query;

      if (!q) return;

      const key = q.queryKey;
      if (key[0] !== 'searchNotesAgGridV2') {
        return;
      }

      // Check if it is your SSRM query AND it finished fetching
      if (
        key[0] === 'searchNotesAgGridV2' &&
        key[1]?.screenSessionId === screenSessionId &&
        q.state.fetchStatus === 'idle' &&
        q.state.isInvalidated // Fetch succeeded
      ) {
        gridRef.current?.api.refreshServerSide({ purge: true });
      }
    });

    return () => unsubscribe();
  }, [queryClient, screenSessionId, gridRef]);

  const datasource = useMemo(
    () => ({
      getRows: async (params: any) => {
        try {
          const { startRow, endRow, filterModel, sortModel } = params.request;
          const response = await queryClient.fetchQuery({
            queryKey: ['searchNotesAgGridV2', { startRow, endRow, filterModel, sortModel, screenSessionId }],
            queryFn: async () => {
              // Get user's timezone from browser
              const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

              const agGridRequest = {
                startRow,
                endRow,
                filterModel,
                sortModel,
                query: subjectFilter,
                globalPartnerId: null,
                screenSessionId,
                timezone: userTimezone, // Add user's timezone for date filtering
                ...(usedInBr && !isFederated ? { brUid: techId } : { partnerUid: techId }),
                ...(isFederated ? (brUId && brUId.length > 0 ? { brUid: brUId } : { partnerUid: partnerUId }) : {}),
              };
              // eslint-disable-next-line no-console
              console.log(
                'ContactNotes sending request with session ID:',
                screenSessionId,
                'Timezone:',
                userTimezone,
                'Request:',
                agGridRequest,
              );
              const res = await searchNotes(agGridRequest);
              return res;
            },
            //Adding cacheTime so that subscriber is able to invalidate and refetch
            cacheTime: Number.POSITIVE_INFINITY,
          });
          dispatch(initContactNotes(response.rows));

          if (response.totalCount === 0) {
            params.api.showNoRowsOverlay();
          } else {
            params.api.hideOverlay();
          }

          params.success({
            rowData: convertRowData(response.rows || []),
            rowCount: response.totalCount, // Use lastRow for infinite scroll
          });
        } catch {
          // Error is handled by searchNotes, just need to fail the request
          params.fail();
        }
      },
    }),
    [
      convertRowData,
      filters,
      EnvConfig.config.useAgGridV2HomeSearch,
      subjectFilter,
      dateRange,
      screenSessionId,
      isFederated,
      searchNotes,
    ],
  );

  useEffect(() => {
    // When the datasource object changes (e.g. filters change),
    // we need to tell the grid to fetch data again.
    // Purging the cache ensures it uses the new datasource parameters.
    gridRef.current?.api?.refreshServerSide({ purge: true });
  }, [datasource, isFederated]);

  const columnDefs = useMemo<ColDef[]>(() => {
    const disableSort = subjectFilter !== '';
    return [
      {
        headerName: '',
        cellRenderer: 'CheckboxCellRenderer',
        cellRendererParams: {
          isSelected: (node: any) => {
            const id = node.data?.contactNoteId;
            const isOriginallyLinked =
              effectiveLinkedIds.includes(node.data?.[linkedIdField]) && !unlinkedNoteIds.has(id);
            return isOriginallyLinked || selectedNotes.some((note) => note.contactNoteId === id);
          },
          isRowSelectable: (node: any) =>
            (!effectiveLinkedIds.includes(node.data?.[linkedIdField]) && !allowUnlink) || allowUnlink,
          allowUnlink: allowUnlink,
        },
        pinned: 'left',
        width: 48,
        hide: !isFederated,
        resizable: false,
        sortable: false,
      },
      {
        field: 'contactNoteId',
        headerName: 'Contact note id',
        filter: 'agTextColumnFilter',
        pinned: 'left',
        sortable: false,
        filterParams: {
          maxNumConditions: 2,
          filterOptions: ['equals', 'contains'],

          buttons: ['apply', 'reset'],
          closeOnApply: true,
        },
        tooltipValueGetter: tooltipValueGetter,
      },
      {
        field: 'startDateTime',
        headerName: 'Contact date',
        filter: DateFilter,
        filterParams: {
          buttons: ['apply', 'reset'], // Show reset button
          closeOnApply: true,
          registerFilter: (filterInstance: any) => {
            customFiltersRef.current.set('startDateTime', filterInstance);
          },
        },
        tooltipValueGetter: tooltipValueGetter,
        width: 150,
      },
      {
        field: 'relatedBRs',
        headerName: 'BR',
        filter: 'agTextColumnFilter',
        filterParams: {
          maxNumConditions: 2,
          filterOptions: ['equals'],
          defaultOption: 'equals',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
        },
        sortable: false,
        tooltipValueGetter: tooltipValueGetter,
      },
      {
        field: 'relatedPartners',
        headerName: 'Client representative',
        filter: PartnerColumnFilter, // Custom searchable dropdown filter
        filterParams: {
          suppressAndOrCondition: true, // Don't show AND/OR condition selector
          buttons: ['apply', 'reset'], // Show reset button
          registerFilter: (filterInstance: any) => {
            customFiltersRef.current.set('relatedPartners', filterInstance);
          },
          closeOnApply: true,
        },
        sortable: false,
        tooltipValueGetter: tooltipValueGetter,
      },
      {
        field: 'additionalAttendees',
        headerName: 'Bank representative',
        filter: AdditionalAttendeeFilter,
        filterParams: {
          suppressAndOrCondition: true, // Don't show AND/OR condition selector
          buttons: ['apply', 'reset'], // Show reset button
          registerFilter: (filterInstance: any) => {
            customFiltersRef.current.set('additionalAttendees', filterInstance);
          },
          closeOnApply: true,
        },
        sortable: false,
        tooltipValueGetter: tooltipValueGetter,
      },
      {
        field: 'subject',
        headerName: 'Subject',
        filter: 'agTextColumnFilter',
        filterParams: {
          maxNumConditions: 2,
          buttons: ['apply', 'reset'],
          filterOptions: ['equals', 'contains'],
          closeOnApply: true,
        },
        sortable: !disableSort,
        tooltipValueGetter: tooltipValueGetter,
      },
      { field: 'notes', headerName: 'Notes', filter: false, sortable: false, tooltipValueGetter: tooltipValueGetter },
      {
        field: 'contactType',
        headerName: 'Communication channel',
        filter: 'agSetColumnFilter',
        valueFormatter: (params) => (params.value ? contactTypeLabel(params.value) : '-'),
        filterParams: {
          buttons: ['apply', 'reset'],
          values: contactTypeFilteredOptions.map((o) => o.value),
          valueFormatter: (params: any) => (params.value ? contactTypeLabel(params.value) : '-'),
          closeOnApply: true,
        },

        sortable: false,
        tooltipValueGetter: (params: any) => (params.value ? contactTypeLabel(params.value) : '-'),
      },
      {
        field: 'topics',
        headerName: t('contactNote.form.topics'),
        filter: 'agSetColumnFilter',
        valueFormatter: (params) => topicsLabel(params.data?.topicsLabels),
        filterParams: {
          buttons: ['apply', 'reset'],
          values: topicsGridOptions.map((option) => option.value),
          valueFormatter: (params: any) => {
            const option = topicsGridOptions.find((item) => item.value === params.value);
            return option?.label ?? params.value ?? '-';
          },
          closeOnApply: true,
        },
        sortable: !disableSort,
        tooltipValueGetter: (params: any) => topicsLabel(params.data?.topicsLabels),
      },
      {
        field: 'topicOther',
        headerName: t('contactNote.form.topicsOther'),
        filter: false,
        sortable: false,
        valueFormatter: (params) => params.value ?? '',
        tooltipValueGetter: (params: any) => params.value || null,
      },
      {
        field: 'createdByFullName',
        headerName: 'Creator',
        filter: CreatorColumnFilter,
        filterParams: {
          buttons: ['apply', 'reset'], // Show reset button
          closeOnApply: true,
          registerFilter: (filterInstance: any) => {
            customFiltersRef.current.set('createdByFullName', filterInstance);
          },
        },
        sortable: !disableSort,
        tooltipValueGetter: tooltipValueGetter,
      },
      {
        field: 'organisationalUnits',
        headerName: 'Org Unit',
        sortable: true,
        filter: 'agSetColumnFilter',
        valueFormatter: (params) => topicsLabel(params.data?.organisationalUnits),
        filterParams: {
          buttons: ['apply', 'reset'],
          values: organisationalUnitDropdownValues.map((option) => option.value),
          valueFormatter: (params: any) => {
            const option = organisationalUnitDropdownValues.find((item) => item.value === params.value);
            return option?.label ?? params.value ?? '-';
          },
          closeOnApply: true,
        },
        tooltipValueGetter: (params) => {
          const ous = params.data?.organisationalUnits;
          return Array.isArray(ous) ? ous.join(', ') : '';
        },
      },
      {
        field: 'source',
        headerName: 'Source',
        filter: 'agTextColumnFilter',
        filterParams: {
          maxNumConditions: 2,
          filterOptions: ['equals', 'contains'],
          buttons: ['apply', 'reset'],
          closeOnApply: true,
        },
        sortable: false,
        tooltipValueGetter: tooltipValueGetter,
      },
      {
        field: 'tripId',
        headerName: 'Trip id',
        filter: 'agTextColumnFilter',
        filterParams: {
          maxNumConditions: 2,
          filterOptions: ['equals', 'contains'],
          buttons: ['apply', 'reset'],
          closeOnApply: true,
        },
        sortable: false,
        tooltipValueGetter: tooltipValueGetter,
      },
      {
        field: 'attachments',
        headerName: 'Doc available',
        filter: false,
        sortable: true,
        cellRenderer: (params: any) => {
          const hasAttachments = params.value && params.value.length > 0;
          return hasAttachments ? 'Yes' : '-';
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        valueFormatter: (params) =>
          params.value.status
            ? translateStatusEnum(params.value.status, params.value.createdDateTime, params.value.tripId) ||
              (params.value.status === 'DRAFT' ? 'EDITABLE' : params.value.status)
            : 'EDITABLE',

        filter: 'agSetColumnFilter',
        filterParams: {
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          values: ContactNoteStatusOptions.map((o) => o.value),
          valueFormatter: (params: any) =>
            params.value
              ? ContactNoteDialogLabelStatus?.[params.value as keyof typeof ContactNoteDialogLabelStatus]
              : '-',
        },
        sortable: false,
        tooltipValueGetter: tooltipValueGetter,
      },
      {
        field: 'qualifiedClientContact',
        headerName: 'Qualified contact',
        filter: 'agSetColumnFilter',
        valueFormatter: (params) => (params.value ? 'Yes' : 'No'),
        filterParams: {
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          values: [true, false],
          valueFormatter: (params: any) => (params.value ? 'Yes' : 'No'),
        },
      },
    ];
  }, [
    tooltipValueGetter,
    contactTypeFilteredOptions,
    topicsGridOptions,
    topicsLabel,
    subjectFilter,
    isFederated,
    contactTypeLabel,
    t,
    selectedNotes,
    unlinkedNoteIds,
    organisationalUnitOptions,
    effectiveLinkedIds,
    linkedIdField,
  ]);

  const handleReset = useCallback(() => {
    gridRef.current?.api.setFilterModel(null);
    dispatch(toggleContactNoteDrawer(false));

    dispatch(setCnFilters({}));
    dispatch(setCnSubjectSearch(''));
    setSearchInput('');
  }, [dispatch, gridRef]);

  const linkedNotesComponent = useMemo(() => {
    if (selectedNotes.length === 0) return null;

    return (
      <LinkedNotes
        notes={selectedNotes}
        onDelete={handlePillDelete}
        onClear={handleClearAll}
        allowUnlink={allowUnlink}
      />
    );
  }, [selectedNotes, allowUnlink, handlePillDelete, handleClearAll]);

  return (
    <Box sx={{ padding: 3, paddingBottom: 0 }}>
      <Grid container>
        {EnvConfig.config.createContactNoteNewUiEnabled && (
          <>
            <ErrorDialog
              error={errorObject}
              showDialog={showErrorDialog}
              onDialogClose={() => setShowErrorDialog(false)}
            />
            {toastMessage.show && (
              <ZfToast
                position="top-center"
                show={toastMessage.show}
                type={toastMessage.type || 'success'}
                titleText={toastMessage.titleText}
                description={toastMessage.description}
                actionButtonText={toastMessage.actionButtonText}
                onToastActionClick={() => toastMessage.onActionClick?.()}
                onToastDismiss={() => dispatch(setToastMessage({ ...toastMessage, show: false }))}
                autoDismissDuration={10_000}
                data-testid="contact-note-toast"
              />
            )}
            <ZfStack spacing="medium" style={{ flex: 1 }}>
              <ZfStack direction="row" justify="flex-end" spacing="extra-small" style={{ flex: 1 }}>
                <ZfTextButton
                  disabled={isLoading || !user?.hasNoteCreationAccess}
                  size="medium"
                  onClick={() => dispatch(setNoteDisplayDialog(DisplayDialogs.NEW_NOTE_VIEW))}
                  data-testid="create-note-button"
                >
                  {t('contactNote.createContactNote')}
                </ZfTextButton>
                {isFederated && (
                  <ZfTextButton
                    size="medium"
                    hierarchy="tertiary"
                    leadingIcon="cached"
                    disabled={isResetTableDisabled}
                    onClick={handleReset}
                  >
                    {t('contactNote.home.filters.label.resetTable')}
                  </ZfTextButton>
                )}
              </ZfStack>
              {!isFederated && (
                <ZfStack direction="row" spacing="extra-large" justify="space-between" style={{ flex: 1 }}>
                  <ZfBox style={{ minWidth: 400, width: '100%', maxWidth: 800 }}>
                    <ZfInput
                      style={{ flex: 1 }}
                      type="search"
                      placeholder={t('contactNote.search.placeholder')}
                      onInputChange={(e) => {
                        if (e.detail === '') {
                          dispatch(setCnSubjectSearch(e.detail));
                          dispatch(setCnFilters({}));
                          dispatch(setCnPagination(0));
                          dispatch(setCnError(''));
                          if (EnvConfig.config.createContactNoteNewUiEnabled) {
                            setOrder('desc');
                            setOrderBy('startDateTime');
                          }
                        }
                        setSearchInput(e.detail);
                      }}
                      value={searchInput}
                      onSearchSubmit={(e) => {
                        setSearchInput(e.detail);
                        dispatch(setCnSubjectSearch(e.detail));
                        dispatch(setCnFilters({}));
                        dispatch(setCnPagination(0));
                        dispatch(toggleContactNoteDrawer(false));
                        if (EnvConfig.config.createContactNoteNewUiEnabled) {
                          if (e.detail === '') {
                            setOrder('desc');
                            setOrderBy('startDateTime');
                          } else {
                            setOrder('');
                            setOrderBy('');
                          }
                        }
                      }}
                    >
                      <ZfPopover slot="trailing" alignment="start">
                        <ZfIcon slot="anchor" size="medium" name="info" style={{ cursor: 'pointer' }}></ZfIcon>

                        <ZfStack spacing="extra-small" style={{ textWrap: 'wrap' }}>
                          <ZfTypography style={{ whiteSpace: 'pre-line' }} variant="label-small" color="emphasis">
                            {t('contactNote.search.popoverTitle')}
                          </ZfTypography>

                          <ZfTypography style={{ whiteSpace: 'pre-line' }} variant="label-tiny" color="normal">
                            {t('contactNote.search.popoverContent')}
                          </ZfTypography>
                        </ZfStack>
                      </ZfPopover>
                    </ZfInput>
                  </ZfBox>
                  <ZfTextButton
                    size="medium"
                    hierarchy="tertiary"
                    leadingIcon="cached"
                    disabled={isResetTableDisabled}
                    onClick={handleReset}
                  >
                    {t('contactNote.home.filters.label.resetTable')}
                  </ZfTextButton>
                </ZfStack>
              )}
            </ZfStack>

            <EnhancedContactNotesFormDialog
              show={showDialog}
              edit={displayDialog === DisplayDialogs.EDIT_VIEW}
              onClose={() => dispatch(setNoteDisplayDialog(DisplayDialogs.DEFAULT_NOTE_VIEW))}
              isFederated={isFederated}
              brUId={brUId}
              partnerUId={partnerUId}
            />
          </>
        )}

        {!EnvConfig.config.createContactNoteNewUiEnabled && (
          <>
            <Grid item xs={5}>
              <TextFilter
                filterName="Search"
                placeholderText="Search by subject, note, tripID, contact note id or legacy id"
                dispatchAction={(val: string) => {
                  dispatch(setCnSubjectSearch(val));
                  dispatch(setCnFilters({}));
                  dispatch(setCnPagination(0));
                }}
              />
            </Grid>
            <Grid item xs={1} />

            <Grid item xs={6}>
              <CustomDateRangePicker />
            </Grid>
          </>
        )}
      </Grid>
      <br />
      {linkedNotesComponent}
      <>
        {!EnvConfig.config.createContactNoteNewUiEnabled && (
          <>
            {errorMessage === '' ? (
              <>
                <TableContainer
                  sx={{
                    borderTop: 'none',
                    maxWidth: EnvConfig.config.createContactNoteNewUiEnabled ? 'unset' : `calc(50vw - 80px)`,
                  }}
                >
                  <Table aria-label="ContactNotes table" id="contact-notes-table">
                    <DeprecatedTableHead
                      order={order}
                      setOrder={setOrder}
                      setOrderBy={setOrderBy}
                      orderBy={orderBy}
                      headCells={
                        EnvConfig.config.createContactNoteNewUiEnabled
                          ? ContactNoteZafireHeadCells
                          : ContactNoteHeadCells
                      }
                      headCellContext={HeadCellContext.CONTACT_NOTES}
                    />
                    <TableBody>
                      {isLoading || !contactNotes ? (
                        <SkeletonTableRow
                          id="contactnote-skeleton"
                          rowCount={contactNotes.length > 0 ? contactNotes.length : 6}
                          cellCount={EnvConfig.config.createContactNoteNewUiEnabled ? 11 : 6}
                        />
                      ) : (
                        <ContactNoteRows
                          order={order}
                          orderBy={orderBy}
                          page={page}
                          rowsPerPage={rowsPerPage}
                          filteredContactNotes={filteredContactNotes as ContactNote[]}
                        />
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <ZfStack direction="row" spacing="small">
                  {hasFilter && (
                    <ZfTextButton
                      disabled={isLoading}
                      hierarchy="tertiary"
                      size="medium"
                      style={{ marginTop: 8 }}
                      onClick={() => dispatch(setCnFilters({}))}
                    >
                      {t('contactNote.resetFilter')}
                    </ZfTextButton>
                  )}
                  {filteredContactNotes.length > 10 && (
                    <TablePagination
                      style={{ flex: 1 }}
                      rowsPerPageOptions={[10, 20, 50]}
                      component="div"
                      count={filteredContactNotes.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  )}
                </ZfStack>
              </>
            ) : (
              <Typography>{errorMessage}</Typography>
            )}
          </>
        )}
        {EnvConfig.config.createContactNoteNewUiEnabled && (
          <Box
            sx={{
              '.ag-overlay': {
                pointerEvents: 'revert !important',
              },
              '.ag-header': {
                zIndex: 5,
              },
              '.ag-tooltip': {
                maxWidth: '300px',
                whiteSpace: 'normal',
                overflow: 'visible',
                wordBreak: 'break-word',
              },
              '.ag-body-horizontal-scroll > .ag-body-horizontal-scroll-viewport': {
                height: '4px !important',
                maxHeight: '4px !important',
                minHeight: '4px !important',
              },
              '.ag-cell': {
                whiteSpace: 'pre',
              },
            }}
          >
            <div style={{ width: '100%', height: isFederated ? 'calc(60vh - 140px)' : 'calc(100vh - 140px)' }}>
              <AgGridReact<any>
                localeText={{
                  dateFormatOoo: 'dd.mm.yyyy',
                }}
                getRowId={(params) => `${params.data.contactNoteId}-${params.data.id}`}
                ref={gridRef}
                rowModelType="serverSide"
                blockLoadDebounceMillis={1000}
                columnDefs={columnDefs}
                theme={themeZafire}
                noRowsOverlayComponent="NoRowsOverlay" // Name exactly matches the key below
                components={{ NoRowsOverlay: NoRowsOverlay, CheckboxCellRenderer }}
                noRowsOverlayComponentParams={{
                  filterModel: filterModel,
                  subjectFilter: subjectFilter,
                }}
                pagination={true}
                paginationPageSize={10}
                paginationPageSizeSelector={[10, 20, 50]}
                onRowClicked={(event) => {
                  onRowClicked(event);
                }}
                sideBar={isFederated ? false : sideBar}
                suppressCellFocus
                rowSelection={isFederated ? 'multiple' : 'single'}
                suppressRowClickSelection={true}
                serverSideDatasource={datasource}
                onFilterChanged={onFilterChanged}
                onSortChanged={(params) => {
                  params.api.refreshServerSide({ purge: true });
                }}
              />
            </div>
          </Box>
        )}
      </>
    </Box>
  );
};

export default ContactNotes;
